import { localStorageService } from '@/helpers/storageHelpers';
import { createContext, ReactNode, useContext, useState } from 'react';
import { addMonths } from 'date-fns';

export const DEFAULT_COOKIES_PREFERENCES = {
  strictly_cookies_accepted: true,
  functional_cookies_accepted: true,
  performance_cookies_accepted: true,
  targeting_cookies_accepted: true,
} as const;

export type CookiesPreferencesKeysType = keyof typeof DEFAULT_COOKIES_PREFERENCES;

export type CookiesPreferencesType = {
  [K in CookiesPreferencesKeysType]: boolean;
};

const COOKIES_CONSENT_STORAGE_KEY = 'COOKIES_CONSENT';

export const COOKIES_CONSENT_EXPIRATION_DATE_STORAGE_KEY = 'COOKIES_CONSENT_EXPIRATION_DATE';
const COOKIES_CONSENT_STORAGE_MONTH_TIME = 6;

type CookiesContextType = {
  preferences: CookiesPreferencesType | null | undefined;
  save: (cookies: CookiesPreferencesType) => void;
  reset: () => void;
  clear: () => void;
  acceptAll: () => void;
  isEnabled: (cookies: CookiesPreferencesKeysType) => boolean;
  hasPreferences: boolean;
  defaultPreferences: CookiesPreferencesType;
  hasFunctionalCookiesConsent: boolean;
  hasPerformanceCookiesConsent: boolean;
};

const CookiesContextProvider = createContext<CookiesContextType>({} as CookiesContextType);

export const CookiesProvider = ({ children }: { children?: ReactNode }) => {
  const [cookiesPreferences, setCookiesPreferences] = useState<CookiesPreferencesType>(() => {
    return localStorageService.get<CookiesPreferencesType>(COOKIES_CONSENT_STORAGE_KEY);
  });

  const hasFunctionalCookiesConsent = !!cookiesPreferences?.functional_cookies_accepted;
  const hasPerformanceCookiesConsent = !!cookiesPreferences?.performance_cookies_accepted;

  const save = (preferences: CookiesPreferencesType) => {
    const cookiesExpirationDate = addMonths(new Date(), COOKIES_CONSENT_STORAGE_MONTH_TIME).toISOString();

    localStorageService.set(COOKIES_CONSENT_EXPIRATION_DATE_STORAGE_KEY, cookiesExpirationDate);
    localStorageService.set(COOKIES_CONSENT_STORAGE_KEY, preferences);
    setCookiesPreferences(preferences);
  };

  const acceptAll = () => {
    const preferences = Object.fromEntries(
      Object.keys(DEFAULT_COOKIES_PREFERENCES).map((key) => [key, true])
    ) as CookiesPreferencesType;

    save(preferences);
  };

  const reset = () => {
    save(DEFAULT_COOKIES_PREFERENCES);
  };

  const clear = () => {
    localStorageService.set(COOKIES_CONSENT_EXPIRATION_DATE_STORAGE_KEY, '');
    localStorageService.set(COOKIES_CONSENT_STORAGE_KEY, '');
    setCookiesPreferences(null);
  };

  const isEnabled = (key: CookiesPreferencesKeysType) => {
    if (!cookiesPreferences) return false;

    return cookiesPreferences[key];
  };

  return (
    <CookiesContextProvider.Provider
      value={{
        preferences: cookiesPreferences,
        acceptAll,
        save,
        reset,
        clear,
        isEnabled,
        hasPreferences: !!cookiesPreferences,
        defaultPreferences: DEFAULT_COOKIES_PREFERENCES,
        hasFunctionalCookiesConsent,
        hasPerformanceCookiesConsent,
      }}
    >
      {children}
    </CookiesContextProvider.Provider>
  );
};

export const useCookiesPreferences = () => {
  return useContext(CookiesContextProvider);
};
